import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import PlusIcon from '@crm/shared/assets/icons/plus.svg?react';
import SortIcon from '@crm/shared/assets/icons/sort.svg?react';
import { Button, SvgIcon } from '@crm/shared/ui';
import { DealDetailsBlock } from '@crm/widgets/deal-details-content/ui/DealDetailsBlock';
import { EmptyTasks } from '@crm/widgets/deal-tasks/ui/EmptyTasks';
import { TaskComments } from '@widgets/TaskComments';
import { ModalAddNewTaskView } from 'widgets/Modals/ui/ModalAddNewTask/ModalAddNewTaskView';
import { ModalEditTaskView } from 'widgets/Modals/ui/ModalEditTask/ModalEditTaskView';

import { TaskItem } from './TaskItem';
import { useCreateDealTaskModal } from './useCreateDealTaskModal';
import { useDelayedTaskCompletion } from './useDelayedTaskCompletion';
import { useEditDealTaskModal } from './useEditDealTaskModal';
import { useGetDealTasksMutation } from '../api/useGetDealTasksMutation';
import { useSetDealTaskCompletedMutation } from '../api/useSetDealTaskCompletedMutation';
import { useSwitchDealTaskStatusMutation } from '../api/useSwitchDealTaskStatusMutation';

import type {
    TDeal,
    TDealTaskStatus,
    TDealTasksData,
    TGetDealTasksDirection,
} from '@crm/entities/deal';

const TASK_REMOVE_ANIMATION_DELAY = 220;
const TASK_SORT_LABEL_KEYS = {
    asc: 'crm.dealDetailsContent.tasksBlock.sort.oldestFirst',
    desc: 'crm.dealDetailsContent.tasksBlock.sort.newestFirst',
} as const satisfies Record<TGetDealTasksDirection, string>;

type TDealTasksTabsWithListIds = {
    list?: unknown;
};

interface TaskBlockProps {
    dealId: TDeal['id'];
    className?: string;
    tasksData?: TDealTasksData;
}

export const TaskBlock = ({ className, dealId, tasksData }: TaskBlockProps) => {
    const { t } = useTranslation();
    const createDealTaskModal = useCreateDealTaskModal({ dealId, tasksData });
    const editDealTaskModal = useEditDealTaskModal({ dealId, tasksData });
    const { isPending: isGetDealTasksPending, mutate: getDealTasks } = useGetDealTasksMutation({
        dealId,
    });
    const { mutate: setDealTaskCompleted } = useSetDealTaskCompletedMutation({ dealId });
    const { mutate: switchDealTaskStatus } = useSwitchDealTaskStatusMutation({ dealId });
    const { pendingTaskIds, cancelTaskCompletion, scheduleTaskCompletion } =
        useDelayedTaskCompletion(dealId);
    const [completingTaskIds, setCompletingTaskIds] = useState<Set<string>>(() => new Set());
    const [removedTaskIds, setRemovedTaskIds] = useState<Set<string>>(() => new Set());
    const [updatingStatusTaskIds, setUpdatingStatusTaskIds] = useState<Set<string>>(
        () => new Set()
    );
    const [optimisticTaskStatuses, setOptimisticTaskStatuses] = useState<
        Record<string, TDealTaskStatus>
    >({});
    const [taskSortDirection, setTaskSortDirection] = useState<TGetDealTasksDirection>('asc');
    const removeTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const taskIds = useMemo(() => {
        const list = (tasksData?.tabs as TDealTasksTabsWithListIds | undefined)?.list;

        return Array.isArray(list)
            ? list.filter((taskId): taskId is string => typeof taskId === 'string')
            : [];
    }, [tasksData]);
    const tasksByUid = useMemo(
        () => new Map(tasksData?.tasks?.map((task) => [task.uid, task]) ?? []),
        [tasksData?.tasks]
    );
    const tasks = useMemo(
        () => taskIds.map((taskId) => tasksByUid.get(taskId)).filter(Boolean),
        [taskIds, tasksByUid]
    );
    const visibleTasks = useMemo(
        () => tasks.filter((task) => !removedTaskIds.has(task.uid)),
        [removedTaskIds, tasks]
    );
    const toggleTaskSortDirection = useCallback(() => {
        if (isGetDealTasksPending) {
            return;
        }

        const previousDirection = taskSortDirection;
        const nextDirection: TGetDealTasksDirection = previousDirection === 'desc' ? 'asc' : 'desc';

        setTaskSortDirection(nextDirection);
        getDealTasks(
            {
                payload: {
                    direction: nextDirection,
                },
            },
            {
                onError: () => {
                    setTaskSortDirection(previousDirection);
                },
            }
        );
    }, [getDealTasks, isGetDealTasksPending, taskSortDirection]);

    useEffect(
        () => () => {
            Object.values(removeTimeoutsRef.current).forEach(clearTimeout);
        },
        []
    );

    useEffect(() => {
        setOptimisticTaskStatuses((currentStatuses) => {
            const taskUidSet = new Set(tasks.map((task) => task.uid));
            const nextStatuses = Object.fromEntries(
                Object.entries(currentStatuses).filter(([taskUid]) => taskUidSet.has(taskUid))
            );

            return nextStatuses;
        });
    }, [tasks]);

    const resetTaskRemovingState = useCallback((taskUid: string) => {
        clearTimeout(removeTimeoutsRef.current[taskUid]);
        delete removeTimeoutsRef.current[taskUid];
        setCompletingTaskIds((currentIds) => {
            const nextIds = new Set(currentIds);
            nextIds.delete(taskUid);

            return nextIds;
        });
        setRemovedTaskIds((currentIds) => {
            const nextIds = new Set(currentIds);
            nextIds.delete(taskUid);

            return nextIds;
        });
    }, []);

    const completeTask = useCallback(
        (task: TDealTasksData['tasks'][number]) => {
            const taskUid = task.uid;
            const taskEntityUid = task.context.entityUid;

            if (!taskEntityUid) {
                return;
            }

            if (cancelTaskCompletion(taskUid)) {
                return;
            }

            if (completingTaskIds.has(taskUid)) {
                return;
            }

            scheduleTaskCompletion(taskUid, () => {
                setCompletingTaskIds((currentIds) => new Set(currentIds).add(taskUid));
                removeTimeoutsRef.current[taskUid] = setTimeout(() => {
                    setRemovedTaskIds((currentIds) => new Set(currentIds).add(taskUid));
                }, TASK_REMOVE_ANIMATION_DELAY);

                setDealTaskCompleted(
                    {
                        taskUid: taskEntityUid,
                        payload: {
                            isCompleted: true,
                        },
                    },
                    {
                        onError: () => {
                            resetTaskRemovingState(taskUid);
                        },
                    }
                );
            });
        },
        [
            cancelTaskCompletion,
            completingTaskIds,
            resetTaskRemovingState,
            scheduleTaskCompletion,
            setDealTaskCompleted,
        ]
    );

    const resetTaskStatusState = useCallback((taskUid: string, status?: TDealTaskStatus) => {
        setUpdatingStatusTaskIds((currentIds) => {
            const nextIds = new Set(currentIds);
            nextIds.delete(taskUid);

            return nextIds;
        });

        if (status) {
            setOptimisticTaskStatuses((currentStatuses) => ({
                ...currentStatuses,
                [taskUid]: status,
            }));

            return;
        }

        setOptimisticTaskStatuses((currentStatuses) => {
            const nextStatuses = { ...currentStatuses };
            delete nextStatuses[taskUid];

            return nextStatuses;
        });
    }, []);

    const switchTaskStatus = useCallback(
        (task: TDealTasksData['tasks'][number], status: TDealTaskStatus) => {
            const taskUid = task.uid;
            const taskEntityUid = task.context.entityUid;

            if (!taskEntityUid) {
                return;
            }

            if (updatingStatusTaskIds.has(taskUid)) {
                return;
            }

            const previousStatus = task.context.status;

            setUpdatingStatusTaskIds((currentIds) => new Set(currentIds).add(taskUid));
            setOptimisticTaskStatuses((currentStatuses) => ({
                ...currentStatuses,
                [taskUid]: status,
            }));

            switchDealTaskStatus(
                {
                    taskUid: taskEntityUid,
                    payload: {
                        status,
                    },
                },
                {
                    onError: () => {
                        resetTaskStatusState(taskUid, previousStatus);
                    },
                    onSettled: () => {
                        setUpdatingStatusTaskIds((currentIds) => {
                            const nextIds = new Set(currentIds);
                            nextIds.delete(taskUid);

                            return nextIds;
                        });
                    },
                }
            );
        },
        [resetTaskStatusState, switchDealTaskStatus, updatingStatusTaskIds]
    );

    return (
        <>
            <DealDetailsBlock
                className={className}
                renderHederContent={() => (
                    <div className="flex shrink-0 items-center gap-2">
                        <button
                            type="button"
                            className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2.5 rounded-md bg-background px-3.5 py-1.5 text-smtr-main-tertiary-100 outline outline-border hover:bg-smtr-border-100 disabled:pointer-events-none disabled:opacity-60"
                            disabled={isGetDealTasksPending}
                            onClick={toggleTaskSortDirection}
                        >
                            <div className="justify-start text-xs leading-4 font-normal">
                                {t(TASK_SORT_LABEL_KEYS[taskSortDirection])}
                            </div>
                            <SvgIcon icon={SortIcon} size="12" />
                        </button>
                        <Button
                            type="button"
                            className="border-none bg-smtr-main-accent-50 text-smtr-main-accent-100 hover:bg-blue-100"
                            onClick={createDealTaskModal.onOpenModal}
                        >
                            <SvgIcon icon={PlusIcon} className="size-2.5" />
                            {t('crm.dealDetailsContent.tasksBlock.actions.create')}
                        </Button>
                    </div>
                )}
                title={t('crm.dealDetailsContent.tasksBlock.title')}
            >
                <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col gap-2 overflow-x-hidden overflow-y-auto pt-1 pr-1">
                    {visibleTasks.length > 0 ? (
                        visibleTasks.map((task) => (
                            <TaskItem
                                key={task.uid}
                                employees={tasksData?.employees ?? []}
                                tags={tasksData?.tags ?? []}
                                isCompleting={completingTaskIds.has(task.uid)}
                                isCompletionPending={pendingTaskIds.has(task.uid)}
                                isStatusUpdating={updatingStatusTaskIds.has(task.uid)}
                                status={optimisticTaskStatuses[task.uid]}
                                task={task}
                                onComplete={completeTask}
                                onOpenEdit={editDealTaskModal.onOpenModal}
                                onSwitchStatus={switchTaskStatus}
                            />
                        ))
                    ) : (
                        <EmptyTasks />
                    )}
                </div>
            </DealDetailsBlock>
            <ModalAddNewTaskView
                {...createDealTaskModal}
                smetterCssClassName="smetter-css"
                portalFileOverlays
            />
            <ModalEditTaskView
                {...editDealTaskModal}
                commentsPanel={
                    editDealTaskModal.modalMode === 'editTask' &&
                    editDealTaskModal.taskData?.type === 'custom' &&
                    editDealTaskModal.pickedTaskUid &&
                    editDealTaskModal.taskData.context.entity_uid ? (
                        <TaskComments
                            key={editDealTaskModal.pickedTaskUid}
                            taskId={editDealTaskModal.taskData.context.entity_uid}
                        />
                    ) : undefined
                }
                smetterCssClassName="smetter-css"
                portalFileOverlays
            />
        </>
    );
};
